import { KycRouteError } from '../routes/kycError';
import { jwtDecode } from '../utils/jwtDecode';

export type OnboardingContext = { purpose: 'KYC' } | { purpose: 'ACCOUNT_UPGRADE'; fromAccountType: string; toAccountType: string };
export type OnboardingSession = {
  token: string;
  cognitoId: string;
  context: OnboardingContext;
  expiresAt: number;
  generation: number;
};

const SESSION_KEY = 'onboarding.session';
const RETURN_KEY = 'onboarding.returnPath';
export const ONBOARDING_SESSION_CHANGED = 'onboarding-session-changed';
let pending: { code: string; context: OnboardingContext } | undefined;
let redemption: Promise<OnboardingSession> | undefined;
let generation = 0;
let newLaunch = false;
export const hasNewOnboardingLaunch = () => newLaunch;

const invalidLink = () => new KycRouteError('invalid-link', undefined);
const sameContext = (a: OnboardingContext, b: OnboardingContext) =>
  a.purpose === b.purpose &&
  (a.purpose === 'KYC' ||
    (b.purpose === 'ACCOUNT_UPGRADE' && a.fromAccountType === b.fromAccountType && a.toAccountType === b.toAccountType));

export function getOnboardingContext(pathname: string): OnboardingContext {
  const upgrade = /^\/onboard\/upgrade\/([A-Z0-9_]{1,64})\/([A-Z0-9_]{1,64})\/?$/.exec(pathname);
  if (upgrade) return { purpose: 'ACCOUNT_UPGRADE', fromAccountType: upgrade[1], toAccountType: upgrade[2] };
  if (pathname === '/onboard' || (pathname.startsWith('/onboard/') && !pathname.startsWith('/onboard/upgrade'))) {
    return { purpose: 'KYC' };
  }
  throw invalidLink();
}

export function safeOnboardingReturnPath(value: string | null): string | undefined {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    [...value].some(char => char.charCodeAt(0) < 32)
  )
    return;
  try {
    const url = new URL(value, window.location.origin);
    if (
      url.origin !== window.location.origin ||
      url.hash ||
      [...url.searchParams.keys()].some(key => /^(token|handoff|code)$/i.test(key))
    )
      return;
    return `${url.pathname}${url.search}`;
  } catch {
    return;
  }
}

export function getOnboardingSearch(url: URL): string {
  const search = new URLSearchParams(url.search);
  for (const key of ['token', 'handoff', 'code']) search.delete(key);
  if (search.has('webRedirectUrl')) {
    const returnPath = safeOnboardingReturnPath(search.get('webRedirectUrl'));
    search.delete('webRedirectUrl');
    if (returnPath) search.set('webRedirectUrl', returnPath);
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const isCurrentOnboardingSession = (session: OnboardingSession) =>
  session.generation === generation && session.expiresAt > Date.now();

export function clearOnboardingSession(expectedGeneration = generation) {
  if (expectedGeneration !== generation) return;
  generation++;
  newLaunch = false;
  pending = undefined;
  redemption = undefined;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Storage may be disabled; an in-memory redemption must still be invalidated.
  }
  window.dispatchEvent(new Event(ONBOARDING_SESSION_CHANGED));
}

export function getOnboardingTokenGeneration(token: string): number | undefined {
  try {
    if (JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null')?.token === token) return generation;
  } catch {
    return;
  }
}

export function rejectOnboardingToken(token: string, requestGeneration: number | undefined) {
  if (requestGeneration !== undefined && getOnboardingTokenGeneration(token) === requestGeneration) {
    clearOnboardingSession(requestGeneration);
  }
}

function saveSession(token: string, context: OnboardingContext, expectedId?: string): OnboardingSession {
  const claims = jwtDecode(token);
  if (
    typeof claims.sub !== 'string' ||
    !claims.sub ||
    typeof claims.exp !== 'number' ||
    !Number.isFinite(claims.exp) ||
    claims.exp * 1000 <= Date.now() ||
    (expectedId !== undefined && claims.sub !== expectedId)
  )
    throw invalidLink();
  const session = { token, cognitoId: claims.sub, context, expiresAt: claims.exp * 1000, generation };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export function hasOnboardingLaunchCredentials(value: string): boolean {
  try {
    const url = new URL(value, window.location.origin);
    if (url.pathname !== '/onboard' && !url.pathname.startsWith('/onboard/')) return false;
    const fragment = new URLSearchParams(url.hash.slice(1));
    return ['token', 'handoff', 'code'].some(key => fragment.has(key) || url.searchParams.has(key));
  } catch {
    return false;
  }
}

// Called before importing the app: neither the router nor analytics may see launch credentials.
export function captureOnboardingLaunch() {
  const url = new URL(window.location.href);
  if (url.pathname !== '/onboard' && !url.pathname.startsWith('/onboard/')) return false;
  const fragment = new URLSearchParams(url.hash.slice(1));
  const code = fragment.get('handoff');
  const hasCredential = hasOnboardingLaunchCredentials(url.href);
  const returnPath = safeOnboardingReturnPath(url.searchParams.get('webRedirectUrl'));
  url.search = getOnboardingSearch(url);
  for (const key of ['token', 'handoff', 'code']) fragment.delete(key);
  url.hash = fragment.toString();
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  if (!hasCredential) return false;
  clearOnboardingSession();
  try {
    sessionStorage.removeItem(RETURN_KEY);
    if (returnPath) sessionStorage.setItem(RETURN_KEY, returnPath);
    const context = getOnboardingContext(url.pathname);
    // A legacy ?token= launch is still scrubbed and clears the old session, but never becomes a session.
    // A reload while redeeming must recover by reopening the app, never replay the code.
    if (code !== null && /^[A-Za-z0-9_-]{43}$/.test(code)) pending = { code, context };
  } catch {
    clearOnboardingSession();
  }
  newLaunch = true;
  return true;
}

export async function getOnboardingSession(
  context: OnboardingContext,
  redeem: (code: string, context: OnboardingContext) => Promise<{ accessToken: string; cognitoId: string }>
): Promise<OnboardingSession> {
  const currentGeneration = generation;
  newLaunch = false;
  if (pending) {
    const launch = pending;
    pending = undefined;
    redemption = (async () => {
      try {
        if (!sameContext(launch.context, context)) throw invalidLink();
        const response = await redeem(launch.code, context);
        if (currentGeneration !== generation) throw invalidLink();
        if (typeof response.cognitoId !== 'string' || !response.cognitoId) throw invalidLink();
        return saveSession(response.accessToken, context, response.cognitoId);
      } catch {
        // Never retain server/network errors that could contain credentials, and never retry redemption.
        clearOnboardingSession(currentGeneration);
        throw invalidLink();
      }
    })();
  }
  const activeRedemption = redemption;
  if (activeRedemption) {
    await activeRedemption;
    if (currentGeneration !== generation) throw invalidLink();
  }
  try {
    const stored: OnboardingSession | null = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
    if (!stored || typeof stored.cognitoId !== 'string' || !stored.cognitoId || !sameContext(stored.context, context))
      throw invalidLink();
    return saveSession(stored.token, context, stored.cognitoId);
  } catch {
    clearOnboardingSession(currentGeneration);
    throw invalidLink();
  }
}

export function finishOnboarding(callback: 'kycOnSuccess' | 'kycOnBack') {
  let returnPath: string | undefined;
  try {
    returnPath = safeOnboardingReturnPath(sessionStorage.getItem(RETURN_KEY));
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    // App callbacks still work with storage disabled.
  }
  clearOnboardingSession();
  return returnPath ?? `/${callback}`;
}
